import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, CircularProgress, Typography, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getEventByQrCode } from "../api/event";
import MenuContent from "../components/MenuContent";
import { event } from "../utils/gtag";
import CustomPageContent from "../components/CustomPageContent";
import AgendaContent from "../components/AgendaContent";
import FormScreen from "./FormScreen";
import EventHeader from "../components/EventHeader";
import QuickActionsPanel from "../components/QuickActionsPanel";
import PoweredByTipify from "../components/PoweredByTipify";
import LanguageSelector from "../components/LanguageSelector";

const EventLandingPage = () => {
  const { t } = useTranslation();
  const { id } = useParams(); // La ruta es /event/:id, no /event/:recipientId
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [eventData, setEventData] = useState(null);
  const [businessData, setBusinessData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log("EventLandingPage - Iniciando, id recibido:", id);
        if (!id || id === "undefined" || id === "null") {
          console.log("EventLandingPage - ID no válido");
          setError(t("event.errors.invalidQr"));
          setLoading(false);
          return;
        }

        // Intentar primero como evento
        const eventRes = await getEventByQrCode(id);
        console.log("EventLandingPage - Respuesta del backend:", eventRes);
        
          if (eventRes.status === 200 && eventRes.data) {
            const responseData = eventRes.data;
            if (responseData.tipo === "evento") {
              console.log("EventLandingPage - Evento encontrado:", responseData.evento);
              console.log("EventLandingPage - contenidoIntermedio:", responseData.evento.contenidoIntermedio);
              setEventData(responseData.evento);
              setBusinessData(responseData.business);
              
              // Tracking: evento cargado
              event({
                action: "cargar_evento",
                category: "Eventos",
                label: responseData.evento.contenidoIntermedio?.activo 
                  ? `Tipo: ${responseData.evento.contenidoIntermedio.tipo}` 
                  : "Sin contenido intermedio",
              });
              
              setLoading(false);
              return;
            }
          }

        // Si no se encuentra el evento (404 u otro error), redirigir a FormScreen
        // FormScreen buscará business y tiped
        console.log("EventLandingPage - No se encontró evento con ese QR code, redirigiendo a FormScreen. Status:", eventRes.status);
        setLoading(false);
        navigate(`/${id}`, { replace: true });
        return;
      } catch (err) {
        console.error("Error al cargar datos:", err);
        setError(t("event.errors.loadError"));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  // Si no hay contenido intermedio o es otro tipo, redirigir a FormScreen
  // FormScreen manejará la carga del evento automáticamente
  useEffect(() => {
    if (!loading && eventData && !eventData?.contenidoIntermedio?.activo) {
      // Ya tenemos los datos del evento, pero FormScreen los cargará de nuevo
      // Para evitar doble carga, podríamos pasar los datos, pero por simplicidad
      // dejamos que FormScreen los cargue
      console.log("EventLandingPage - Evento sin contenido intermedio, redirigiendo a FormScreen");
      navigate(`/${id}`, { replace: true });
    }
  }, [loading, eventData, navigate, id]);

  if (loading) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          zIndex: 9999,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100vw",
          height: "100vh",
          margin: 0,
          padding: 0
        }}
      >
        <CircularProgress 
          size={40}
          thickness={4}
          style={{
            width: "80px",
            height: "80px",
            maxWidth: "80px",
            maxHeight: "80px",
            color: "#66bc5f"
          }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <Container style={{ minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
        <Box style={{ position: "absolute", top: "20px", right: "20px" }}>
          <LanguageSelector />
        </Box>
        <Typography variant="h6" color="error">{error}</Typography>
        <Typography variant="body2" style={{ marginTop: "10px" }}>
          {t("event.errors.verifyQr")}
        </Typography>
      </Container>
    );
  }

  // Si hay contenido intermedio activo, mostrar el contenido correspondiente
  if (eventData?.contenidoIntermedio?.activo) {
    if (eventData.contenidoIntermedio.tipo === "menu") {
      return (
        <Container maxWidth="md" style={{ minHeight: "100vh", padding: "20px" }}>
          <EventHeader eventData={eventData} businessData={businessData} />
          <MenuContent 
            menuConfig={eventData.contenidoIntermedio.configuracion}
            eventData={eventData}
            contenidoIntermedio={eventData.contenidoIntermedio}
            onNavigateToRatings={() => {
              // Usar query parameter para indicar que queremos ir directamente a valoración
              window.location.href = `/${id}?ratings=true`;
            }}
          />
          <Box mt={2}>
            <QuickActionsPanel
              eventData={eventData}
              businessData={businessData}
            />
          </Box>
          <PoweredByTipify />
        </Container>
      );
    } else if (eventData.contenidoIntermedio.tipo === "pagina_personalizada") {
      return (
        <Container maxWidth="md" style={{ minHeight: "100vh", padding: "20px" }}>
          <EventHeader eventData={eventData} businessData={businessData} />
          <CustomPageContent
            contenidoIntermedio={eventData.contenidoIntermedio}
            eventData={eventData}
            businessData={businessData}
            onNavigateToRatings={() => {
              // Usar query parameter para indicar que queremos ir directamente a valoración
              window.location.href = `/${id}?ratings=true`;
            }}
          />
          <Box mt={2}>
            <QuickActionsPanel
              eventData={eventData}
              businessData={businessData}
            />
          </Box>
          <PoweredByTipify />
        </Container>
      );
    } else if (eventData.contenidoIntermedio.tipo === "agenda") {
      return (
        <Container maxWidth="md" style={{ minHeight: "100vh", padding: "20px" }}>
          <EventHeader eventData={eventData} businessData={businessData} />
          <AgendaContent
            agendaConfig={eventData.contenidoIntermedio.configuracion}
            eventData={eventData}
            contenidoIntermedio={eventData.contenidoIntermedio}
            onNavigateToRatings={() => {
              // Usar query parameter para indicar que queremos ir directamente a valoración
              window.location.href = `/${id}?ratings=true`;
            }}
          />
          <Box mt={2}>
            <QuickActionsPanel
              eventData={eventData}
              businessData={businessData}
            />
          </Box>
          <PoweredByTipify />
        </Container>
      );
    }
  }

  return null; // Mientras redirige
};

export default EventLandingPage;

